<template>
  <ValidationObserver
    ref="form"
    v-slot="{ invalid, errors }"
    class="sign-up insicht insicht--fade-up"
    tag="form"
    @submit.prevent="onSubmit"
    novalidate
  >
    <h2
      v-if="titleText"
      v-html="titleText"
    />

    <GeneralMessage
      v-if="response.message"
      ref="message"
      :message="response.message"
      :type="response.type"
      @close="response.message = ''"
    />

    <!-- EMAIL -->
    <ValidationProvider
      class="input-wrapper"
      name="email"
      rules="required|email"
      v-slot="{ errors, invalid, validated, dirty }"
      tag="div"
      ref="registerEmail"
      :customMessages="{ required: $t('forms.errors.fieldEmpty'), email: $t('forms.errors.emailInvalid') }"
    >
      <FormInput
        type="email"
        name="email"
        autocomplete="email"
        :placeholder="$t('forms.labels.email')"
        :label="$t('forms.labels.email')"
        v-model="formData.email"
        :error="invalid && validated"
        :disabled="formLocked"
        required
      />
      <span
        class="input-error"
        v-if="invalid && validated"
        v-html="errors[0]"
      />
    </ValidationProvider>

    <ValidationObserver
      class="input-wrapper"
      tag="div"
      v-slot="{ validated, invalid, errors }"
    >
      <div class="input-group">
        <ValidationProvider
          class="input-group__span-6"
          name="firstname"
          rules="required"
          v-slot="{ errors, invalid, validated, dirty }"
          tag="div"
          :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
        >
          <FormInput
            type="text"
            name="firstname"
            :placeholder="$t('forms.labels.firstname')"
            :label="$t('forms.labels.firstname')"
            v-model="formData.firstName"
            :error="invalid && validated"
            :disabled="formLocked"
            required
          />
        </ValidationProvider>

        <ValidationProvider
          class="input-group__span-6"
          name="lastname"
          rules="required"
          v-slot="{ errors, invalid, validated, dirty }"
          tag="div"
          :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
        >
          <FormInput
            type="text"
            name="lastname"
            :placeholder="$t('forms.labels.lastname')"
            :label="$t('forms.labels.lastname')"
            v-model="formData.lastName"
            :error="invalid && validated"
            :disabled="formLocked"
            required
          />
        </ValidationProvider>
      </div>
      <div
        class="input-error input-group__span-12"
        v-if="invalid && validated"
        v-html="combineErrors(errors)"
      />
    </ValidationObserver>

    <ValidationProvider
      class="input-wrapper"
      name="street"
      rules=""
      v-slot="{ errors, invalid, validated, dirty }"
      tag="div"
      :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
    >
      <FormInput
        type="text"
        name="street"
        :placeholder="$t('forms.labels.streetNumber')"
        :label="$t('forms.labels.streetNumber')"
        :labelOption="$t('forms.labels.optional')"
        v-model="formData.street"
        :error="invalid && validated"
        :disabled="formLocked"
      />
      <span
        class="input-error"
        v-if="invalid && validated"
        v-html="errors[0]"
      />
    </ValidationProvider>

    <ValidationObserver
      class="input-wrapper"
      tag="div"
      v-slot="{ validated, invalid, errors }"
    >
      <div class="input-group">
        <ValidationProvider
          class="input-group__span-3"
          name="zip"
          rules="digits:4"
          v-slot="{ errors, invalid }"
          tag="div"
          :customMessages="{ required: $t('forms.errors.fieldEmpty'), digits: $t('forms.errors.zip') }"
        >
          <FormInput
            ref="zip"
            type="text"
            v-model="formData.postcode"
            @keydown="onZipKeydown"
            @input="onZipInput"
            :placeholder="$t('forms.labels.zip')"
            :label="$t('forms.labels.zip')"
            :labelOption="$t('forms.labels.optional')"
            name="zip"
            :minlength="4"
            :maxlength="4"
            inputmode="decimal"
            pattern="[0-9]{4}"
            :error="errors.length > 0"
            :disabled="formLocked"
          />
        </ValidationProvider>
        <ValidationProvider
          class="input-group__span-9"
          name="city"
          rules=""
          v-slot="{ errors, invalid }"
          tag="div"
          :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
        >
          <FormInput
            type="text"
            :placeholder="$t('forms.labels.city')"
            :label="$t('forms.labels.city')"
            :labelOption="$t('forms.labels.optional')"
            name="city"
            v-model="formData.city"
            :error="errors.length > 0"
            :disabled="formLocked"
          />
        </ValidationProvider>
      </div>
      <div
        class="input-error input-group__span-12"
        v-if="invalid && validated"
        v-html="combineErrors(errors)"
      />
    </ValidationObserver>

    <ValidationProvider
      class="input-wrapper"
      name="phone"
      rules="phone"
      v-slot="{ errors, invalid, validated }"
      tag="div"
      :customMessages="{ required: $t('forms.errors.fieldEmpty'), phone: $t('forms.errors.phone') }"
    >
      <FormInput
        type="tel"
        name="phone"
        :placeholder="$t('forms.labels.phone')"
        :label="$t('forms.labels.phone')"
        :labelOption="$t('forms.labels.optional')"
        v-model="formData.phoneNumber"
        :error="errors.length > 0"
        :disabled="formLocked"
        required
      />
      <span
        class="input-error"
        v-if="invalid && validated"
        v-html="errors[0]"
      />
      <!-- <span class="additional-info" v-html="$t('login.phoneHint')"/> -->
    </ValidationProvider>

    <ValidationProvider
      class="input-wrapper"
      name="birthday"
      tag="div"
      rules=""
      v-slot="{ invalid, errors, validated }"
      :customMessages="{ required: $t('forms.errors.date') }"
    >
      <FormInputDate
        :placeholder="$t('forms.labels.birthday')"
        :label="$t('forms.labels.birthday')"
        :labelOption="$t('forms.labels.optional')"
        v-model="formData.birthday"
        :initialValue="formData.birthday"
        min-date=""
        max-date="today"
        :disabled="formLocked"
      />
      <span
        class="input-error"
        v-if="invalid && validated"
        v-html="errors[0]"
      />
    </ValidationProvider>

    <ValidationProvider
      :rules="{ required: { allowFalse: false } }"
      name="checkTerms"
      tag="div"
      v-slot="{ invalid, errors, validated }"
      :customMessages="{ required: $t('login.errorTerms') }"
    >
      <FormInputCheckbox
        name="terms"
        value="checkTerms"
        v-model="formData.checkTerms"
        required
        immediate
        :disabled="formLocked"
      >
        <span v-html="$t('login.termsLabel')"/>
      </FormInputCheckbox>
      <span
        class="input-error"
        v-if="invalid && validated"
        v-html="errors[0]"
      />
    </ValidationProvider>

    <Button
      type="submit"
      level="primary"
      :text="$t('newsletter.subscribeButton')"
      :disabled="formLocked"
    />
  </ValidationObserver>
</template>

<script>
import Vue from "vue"
import GenericMixin from '@/mixins/GenericVueMixin';
import { mapGetters } from "vuex"
import { ValidationProvider, ValidationObserver } from "vee-validate"
import FormInput from "@/components/FormInput"
import FormInputCheckbox from "@/components/FormInputCheckbox"
import FormInputDate from "@/components/FormInputDate"
import Button from '@/components/Button'
import GeneralMessage from '@/components/GeneralMessage'
import scrrroll from 'scrrroll';
import { switchDateFormat } from "@/utils/formatDate"

export default {
	mixins: [
		GenericMixin,
	],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputCheckbox,
    FormInputDate,
		Button,
		GeneralMessage,
	},
	props: {
		confirm: {
			type: String,
			default: '',
		},
		title: {
			type: String,
			default: '',
		},
	},
	data() {
		return {
			formData: {
				firstName: '',
				lastName: '',
				email: '',
        street: '',
        postcode: '',
        city: '',
				checkTerms: false,
				phoneNumber: '',
        birthday: '',
			},
      confirmUrl: this.$root.options.confirmUrl || this.confirm,
      titleText: this.$root.options.title || this.title,
			response: {
				type: 'info',
				message: '',
			},
		}
	},
	computed: {
		...mapGetters("account", [
			"isLoggedIn",
			"accountUserDetail",
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		formLocked() {
			return this.loading;
		},
	},
  watch: {
    isLoggedIn() {
      this.prefillForm()
    }
  },
	methods: {
    combineErrors(errors) {
      const errorMessages = Object.values(errors).filter(value => !!value.join('').trim()).flat()
      return (Array.from(new Set(errorMessages))).join(' ')
    },
    onZipKeydown(e) {
			if (!e.metaKey && !e.ctrlKey && e.key && e.key.length <= 1 && !/[0-9]/.test(e.key)) {
				e.preventDefault()
			}
		},
		onZipInput(value) {
			if (/[^\d]/.test(value)) {
				Vue.set(this.formData, 'postcode', value.replace(/[^\d]/ig,'').substring(0,4));
				this.$refs.zip.$refs.input.value = this.formData.postcode;
			}
		},
		async onSubmit() {
			const isValid = await this.$refs.form.validate()
			if (isValid) {
				this.loading = true;
				document.activeElement.blur();
        this.response.message = '';
				try {
					const response = await this.$store.dispatch('subscribeNewsletter', this.formData)
					this.resetForm();
          this.$emit('subscribed');
          return this.$store.dispatch('redirect', {
						name: 'custom',
						url: this.confirmUrl
					})
				} catch(err) {
					const data = err.response.data;

					if (data?.errors) {
            const errors = Object.entries(data.errors).reduce((e, [k, v]) => {
              return {
                ...e,
                [k]: v.join()
              }
            }, {})
            this.$refs.form.setErrors(errors)
            await this.$nextTick();
            const inputError = this.$refs.form.$el.querySelector('.input-error');

            if (inputError) {
              scrrroll.to(inputError.parentNode)
            }
					} else {
            this.response.message = this.$t('forms.errors.generic');
            this.response.type = 'error';
            await this.$nextTick()
            this.$refs.message.scrollTo();
          }
				}
				this.loading = false;
			} else {
				await this.$nextTick();
				const inputError = this.$refs.form.$el.querySelector('.input-error');

				if (inputError) {
					scrrroll.to(inputError.parentNode)
				}
			}
		},
		resetForm() {
			try {
        this.formData.firstName = '';
        this.formData.lastName = '';
        this.formData.email = '';
        this.formData.street = '';
        this.formData.postcode = '';
        this.formData.city = '';
        this.formData.checkTerms = false;
        this.formData.phoneNumber = '';
        this.formData.birthday = '';
        this.response.message = '';
        this.$refs.form.reset();
        this.loading = false;
        document.activeElement.blur();
			}
			catch(err) {}
		},
    prefillForm() {
      if (this.accountUserDetail) {
        Object.keys(this.formData).forEach(key => {
          if (this.accountUserDetail[key] !== undefined) {
            let value = this.accountUserDetail[key]
            if (key === 'birthday') {
              value = switchDateFormat(value, '.')
            }
            Vue.set(this.formData, key, value);
          }
        });
      }
    }
	},
  mounted() {
    this.prefillForm()
  }
}
</script>
